import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaInbox } from "react-icons/fa";
import { FaUser } from "react-icons/fa";
import { FaShoppingCart } from "react-icons/fa";
import { FaBookOpen } from "react-icons/fa";
import { FaRegCalendarAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useProfileStore } from "../../store/useUserStore";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes } from "react-icons/fa";
import { driver } from "driver.js";
import { useEffect } from "react";

const Footer = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const from = queryParams.get("from");
  const authLogin = localStorage.getItem("parent_token");
  const {
    showLoginModal,
    setShowLoginModal,
    showGuidanceFlow,
    setShowGuidanceFlow,
    guidanceStep,
    setGuidanceStep,
    fetchStudents,
  } = useProfileStore((state: any) => state);

  // Initialize driver.js
  useEffect(() => {
    if (
      showGuidanceFlow &&
      guidanceStep === "profile" &&
      location.pathname === "/school"
    ) {
      const driverObj = driver({
        showProgress: false,
        showButtons: [],
        steps: [
          {
            element: "#profile-button",
            popover: {
              description:
                "<img style='width: 170px; height: 166.65px;' src='/assets/img/1.png' />",
              side: "top",
              align: "end",
              onPopoverRender: (popover: any, { config }: any) => {
                // make popover not white
                const popoverElement = popover.wrapper;
                popoverElement.style.backgroundColor = "transparent";
                // make popover no shadow box
                popoverElement.style.boxShadow = "none";

                const popoverDescription = popoverElement.querySelector(
                  "#driver-popover-description",
                ) as HTMLElement;
                if (popoverDescription) {
                  popoverDescription.style.justifyItems = "end";
                  // popoverDescription.style.transform = 'translateY(84%)';
                }
              },
              showButtons: [],
            },
          },
        ],
        onCloseClick: () => {
          setShowGuidanceFlow(false);
          setGuidanceStep(null);
        },
        onPopoverRender: (popover: any, { config }: any) => {
          // Custom styling for the popover
          const popoverElement = popover.wrapper;
          const descriptionElement = popoverElement.querySelector(
            ".driver-popover-description",
          ) as HTMLElement;

          if (descriptionElement) {
            descriptionElement.style.textAlign = "center";
          }
        },
      });

      driverObj.drive();
    }

    // Academy guidance
    if (
      showGuidanceFlow &&
      guidanceStep === "search-academy" &&
      location.pathname === "/profile"
    ) {
      const driverObj = driver({
        showProgress: false,
        showButtons: [],
        steps: [
          {
            element: "#home-button",
            popover: {
              description:
                "<img style='width: 270px; height: 206.65px;' src='/assets/img/4.png' />",
              side: "top",
              align: "start",
              onPopoverRender: (popover: any, { config }: any) => {
                // make popover not white
                const popoverElement = popover.wrapper;
                popoverElement.style.backgroundColor = "transparent";
                popoverElement.style.boxShadow = "none";

                const popoverDescription = popoverElement.querySelector(
                  "#driver-popover-description",
                ) as HTMLElement;
                if (popoverDescription) {
                  popoverDescription.style.justifyItems = "start";
                  // popoverDescription.style.transform = 'translateY(-100%)';
                }
              },
              showButtons: [],
            },
          },
        ],
        onCloseClick: () => {
          setShowGuidanceFlow(false);
          setGuidanceStep(null);
        },
        onPopoverRender: (popover: any, { config }: any) => {
          // Custom styling for the popover
          const popoverElement = popover.wrapper;

          // Center the text content
          const descriptionElement = popoverElement.querySelector(
            ".driver-popover-description",
          ) as HTMLElement;

          if (descriptionElement) {
            descriptionElement.style.textAlign = "center";
          }
        },
      });

      driverObj.drive();
    }
  }, [
    showGuidanceFlow,
    guidanceStep,
    location.pathname,
    t,
    setShowGuidanceFlow,
    setGuidanceStep,
  ]);

  const isActive = (path: string) => {
    if (path === "/") {
      // Active for home, course, or item pages — also treat `from=cart` as home
      return (
        location.pathname === "/" ||
        location.pathname === "/school" ||
        location.pathname === "/academy" ||
        location.pathname.startsWith("/explore/") ||
        location.pathname.startsWith("/branch/") ||
        (location.pathname.startsWith("/course/") &&
          !location.pathname.includes("/edit") &&
          from !== "cart") ||
        (location.pathname.startsWith("/item/") && from !== "cart") ||
        from === "cart"
      );
    }

    if (path === "/cart") {
      const isOnCartPage = location.pathname.startsWith("/cart");
      const isOnHistoryPage = location.pathname.startsWith("/payment-history");

      const isCourseEdit =
        location.pathname.startsWith("/course/") &&
        location.pathname.includes("/edit");

      return (
        (isOnCartPage || isOnHistoryPage || isCourseEdit) && // treat course edit pages as part of cart tab
        !(from === "cart" && !isOnCartPage && !isCourseEdit)
      );
    } else if (path === "/inbox") {
      const inboxActive = location.pathname.startsWith("/inbox");
      const homeworkActive = location.pathname.startsWith("/homework");

      return inboxActive || homeworkActive;
    }

    return location.pathname.startsWith(path);
  };

  const { lastVisitedCourseId, setLastVisitedCourseId } = useProfileStore(
    (state) => state,
  );

  const handleNavigation = (path: string) => {
    if (path === "/") {
      if (lastVisitedCourseId && from !== "cart") {
        return navigate(`/course/${lastVisitedCourseId}`);
      } else if (from === "cart") {
        setLastVisitedCourseId(null);
      }
    }
    return navigate(path);
  };

  const showLoginFirst = () => {
    setShowLoginModal(true);
  };

  const handleLoginClick = () => {
    navigate("/login");
    setShowLoginModal(false);
  };

  return (
    <>
      <footer className="fixed bottom-0 left-0 right-0 z-[1000] mx-auto max-w-[640px] flex justify-between px-8 md:px-12 py-1 bg-white rounded-[40px] shadow-md pb-[env(safe-area-inset-bottom)]">
        <button
          id="home-button"
          className={`relative w-[56px] h-[56px] text-center rounded-full ${
            isActive("/") ? "bg-theme" : ""
          }`}
          onClick={() => {
            if (!authLogin) {
              return navigate("/");
            }

            // Handle guidance flow for academy
            if (showGuidanceFlow && guidanceStep === "search-academy") {
              setGuidanceStep("type-in-course-code");
              return navigate("/academy");
            }

            fetchStudents();

            handleNavigation("/");
          }}
        >
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 leading-none">
            <FaBookOpen
              className={`mx-auto mb-[2px] text-[18px] ${
                isActive("/") ? "text-white" : "text-gray-400"
              }`}
            />
            <span
              className={`text-[12px] ${
                isActive("/") ? "text-white" : "text-gray-400"
              }`}
            >
              {t("footer.home")}
            </span>
          </div>
        </button>

        <button
          className={`relative w-[62px] h-[62px] text-center rounded-full ${
            isActive("/cart") ? "bg-theme" : ""
          }`}
          onClick={() => {
            if (!authLogin) return showLoginFirst();
            navigate("/cart");
          }}
        >
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 leading-none">
            <FaShoppingCart
              className={`mx-auto mb-[2px] text-[18px] ${
                isActive("/cart") ? "text-white" : "text-gray-400"
              }`}
            />
            <span
              className={`text-[12px] ${
                isActive("/cart") ? "text-white" : "text-gray-400"
              }`}
            >
              {t("footer.cart")}
            </span>
          </div>
        </button>

        <button
          className={`relative w-[62px] h-[62px] text-center rounded-full ${
            isActive("/inbox") ? "bg-theme" : ""
          }`}
          onClick={() => {
            if (!authLogin) return showLoginFirst();
            navigate("/inbox");
          }}
        >
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 leading-none">
            <FaInbox
              className={`mx-auto mb-[2px] text-[18px] ${
                isActive("/inbox") ? "text-white" : "text-gray-400"
              }`}
            />
            <span
              className={`text-[12px] ${
                isActive("/inbox") ? "text-white" : "text-gray-400"
              }`}
            >
              {t("footer.inbox")}
            </span>
          </div>
        </button>

        <button
          className={`relative w-[62px] h-[62px] text-center rounded-full ${
            isActive("/schedule") ? "bg-theme" : ""
          }`}
          onClick={() => {
            if (!authLogin) return showLoginFirst();
            navigate("/schedule");
          }}
        >
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 leading-none">
            <FaRegCalendarAlt
              className={`mx-auto mb-[2px] text-[18px] ${
                isActive("/schedule") ? "text-white" : "text-gray-400"
              }`}
            />
            <span
              className={`text-[12px] ${
                isActive("/schedule") ? "text-white" : "text-gray-400"
              }`}
            >
              {t("footer.schedule")}
            </span>
          </div>
        </button>

        <button
          id="profile-button"
          className={`relative w-[62px] h-[62px] text-center rounded-full ${
            isActive("/profile") || location.pathname.startsWith("/children/")
              ? "bg-theme"
              : ""
          }`}
          onClick={() => {
            if (!authLogin) return showLoginFirst();

            // Handle guidance flow
            if (showGuidanceFlow && guidanceStep === "profile") {
              setGuidanceStep("add-child");
            }

            navigate("/profile");
          }}
        >
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 leading-none">
            <FaUser
              className={`mx-auto mb-[2px] text-[18px] ${
                isActive("/profile") ||
                location.pathname.startsWith("/children/")
                  ? "text-white"
                  : "text-gray-400"
              }`}
            />
            <span
              className={`text-[12px] ${
                isActive("/profile") ||
                location.pathname.startsWith("/children/")
                  ? "text-white"
                  : "text-gray-400"
              }`}
            >
              {t("footer.profile")}
            </span>
          </div>
        </button>
      </footer>

      {/* Login First Modal */}
      <AnimatePresence>
        {showLoginModal && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-[2000]"
              onClick={() => setShowLoginModal(false)}
            />

            {/* Modal */}
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 500 }}
              className="fixed bottom-0 left-0 right-0 z-[2001] mx-auto max-w-[640px] bg-white rounded-t-3xl p-6 shadow-lg"
            >
              {/* Close Button */}
              <button
                onClick={() => setShowLoginModal(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              >
                <FaTimes className="text-xl" />
              </button>

              {/* Content */}
              <div className="text-center pt-4">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  {t("explore.loginRequired")}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t("explore.loginFirstMessage")}
                </p>

                {/* Login Button */}
                <button
                  onClick={handleLoginClick}
                  className="w-full bg-theme text-white py-3 px-6 rounded-full font-medium hover:bg-opacity-90 transition-colors"
                >
                  {t("explore.login")}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Footer;
